import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  InputLabel,
  Tooltip,
  Switch,
  Divider,
} from "@mui/material";
import styled from "@emotion/styled";
import { makeStyles } from "@mui/styles";
import { FontFamily } from "assets";
import Colors from "assets/Style/Colors";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import { CircleLoading } from "components/Loaders";
import Pagination from "components/Pagination";
import { PrimaryButton } from "components/Buttons";
import InputField from "components/Input";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomerServices from "services/Customer";
import SystemServices from "services/System";
import UserServices from "services/User";
import { useAuth } from "context/UseContext";
import CloseIcon from "@mui/icons-material/Close";
import DropdownMenu from "components/DropDown";

const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    backgroundColor: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",
    padding: "5px !important",

    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "8px",
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
    "& svg": {
      width: "40px !important",
      height: "40px !important",
    },
  },
  anchorLink: {
    textDecoration: "underline",
    color: Colors.twitter,
    cursor: "pointer",
  },
});

function MobilePermission() {
  const classes = useStyles();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const { user } = useAuth();

  const tableHead = [
    "Select",
    "Creator",
    "C.Name",
    "C.ID",
    "Email",
    "Nationality",
    "Type",
    "Region",
    "Branch",
    "Action",
  ];

  const [visibleColumns, setVisibleColumns] = useState([
    ...Array(tableHead?.length).keys(),
  ]);
  const [selectedRm, setSelectedRmId] = useState([]);
  const [showAssigneeLine, setShowAssigneeLine] = useState(false);

  const {
    register: register2,
  } = useForm();

  const [loader, setLoader] = useState(false);
  const [customerList, setCustomersList] = useState();
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [limitPage, setLimitPage] = useState(1000);
  const [currentPage, setCurrentPage] = useState(1);

  const [wholeCustomers, setWholeCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeMenu, setActiveMenu] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [users, setUsers] = useState([]);

  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permLoading, setPermLoading] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [perm, setPerm] = useState({
    dashboard_module: false,
    vehicle_module: false,
    vcc_module: false,
    payment_module: false,
    gate_pass_module: false,
    statement_module: false,
    sub_customer_module: false,
  });

  const mapPermFromApi = (apiPerm) => ({
    dashboard_module: !!apiPerm?.dashboard_module,
    vehicle_module: !!apiPerm?.vehicle_module,
    vcc_module: !!apiPerm?.vcc_module,
    payment_module: !!apiPerm?.payment_module,
    gate_pass_module: !!apiPerm?.gate_pass_module,
    statement_module: !!apiPerm?.statement_module,
    sub_customer_module: !!apiPerm?.sub_customer_module,
  });

  const openPermissionDialog = (userItem) => {
    setPermUser({ id: userItem?.id, name: userItem?.name });
    setPermDialogOpen(true);
  };

  const openPermissionDialogFromRow = async (userItem) => {
    setPermUser({ id: userItem?.id, name: userItem?.name });
    setShowAssigneeLine(true);
    setPermDialogOpen(true);
    setPermLoading(true);

    try {
      const payload = { customer_id: userItem?.id };
      console.log(payload, 'paylaod')
      const res = await UserServices.getMobilePermissions(payload);
      console.log(res, 'res')
      const current = mapPermFromApi(res?.data?.dataValues);
      setPerm(current);
    } catch (err) {
      ErrorToaster(err || "Failed to load permissions");
      setPerm(mapPermFromApi({}));
    } finally {
      setPermLoading(false);
    }
  };

  const openPermissionDialogBulk = () => {
    setPermUser(null);
    setShowAssigneeLine(false);
    setPerm(mapPermFromApi({}));
    setPermDialogOpen(true);
    setPermLoading(false);
  };

  const closePermissionDialog = () => {
    setPermDialogOpen(false);
    setPermUser(null);
  };

  const handleAssignPermissions = async () => {
    try {
      const targetUserIds = showAssigneeLine && permUser
        ? [permUser.id]
        : selectedVehicles;

      const body = { ...perm, customer_id: targetUserIds };
      const response = await UserServices.updateMobilePermission(body);

      setPermDialogOpen(false);
      if (response?.success) {
        SuccessToaster("Permissions updated");
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getUsers = async () => {
    setLoader(true);
    try {
      const limit = limitPage;
      let params = { limit };
      const { data } = await UserServices.getUsers(params);
      setUsers(data?.users?.rows || []);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const getBranches = async () => {
    setLoader(true);
    try {
      const { data } = await SystemServices.getBranches();
      setBranches(data?.branches);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const getcustomerList = async (page, limit, filter) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? (limit) : pageLimit;
      const Filter = { ...filters, ...filter };

      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);

      let params = { page: Page, limit: Limit, ...Filter };
      const { data } = await CustomerServices.getGalaxyCustomerList(params);

      setCustomersList(data?.customers?.rows);
      setSelectAll(false);
      setTotalCount(data?.customers?.count);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const handleAllCheck = (value) => {
    setSelectAll(!selectAll);

    if (value && customerList?.length) {
      const filteredCustomerIds =
        user?.role_id === 1000
          ? customerList.map((d) => d.id)
          : customerList
              .filter((d) =>
                (d?.relationManagers || []).some((rm) => rm?.id === user?.id),
              )
              .map((d) => d.id);

      setWholeCustomers([...selectedVehicles, ...filteredCustomerIds]);
      setSelectedVehicles(filteredCustomerIds);
    } else {
      const filteredArray = wholeCustomers.filter(
        (item) => !selectedVehicles.includes(item)
      );
      setSelectedVehicles(filteredArray);
    }
  };

  const handleFilter = async (data) => {
    if (isSearching) return;
    setLoader(true);
    setIsSearching(true);
    try {
      await getcustomerList(1, "", data);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setIsSearching(false);
      setLoader(false);
    }
  };

  const handleCheck = (data) => {
    try {
      const allowedUserIds = [56197, 50917, 56212];
      const dataRmIds = (data?.relationManagers || [])
        .map((rm) => rm?.id)
        .filter((v) => v != null);
      if (
        user?.role_id !== 1000 &&
        !allowedUserIds.includes(user?.id) &&
        !dataRmIds.includes(user?.id)
      ) {
        ErrorToaster("You are not authorized to select this customer.");
        return;
      }

      const shallowCopy = [...selectedVehicles];
      const idx = shallowCopy.findIndex((e) => e === data?.id);
      if (idx === -1) shallowCopy.push(data?.id);
      else shallowCopy.splice(idx, 1);

      setWholeCustomers([...selectedVehicles, ...shallowCopy]);
      setSelectedVehicles(shallowCopy);
      setSelectedRmId(dataRmIds);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const handleColumnChange = (event) => {
    const selectedColumns = event.target.value ;
    const sortedColumns = selectedColumns.sort((a, b) => a - b);
    setVisibleColumns(sortedColumns);
  };

  const handleMenuToggle = (menuId, isOpen) => {
    setActiveMenu(isOpen ? menuId : null);
  };

  const renderCellContent = (colIndex, item) => {
    switch (colIndex) {
      case 0:
        return (
          <Checkbox
            className="pdf-hide"
            disabled={item?.customerProfile?.customer_type_id == 4}
            checked={selectedVehicles.findIndex((e) => e === item?.id) !== -1}
            onChange={() => handleCheck(item)}
          />
        );
      case 1:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.creator ? item?.creator?.name : "Self"}
              arrow
              placement="top"
              slotProps={{
                popper: { modifiers: [{ name: "offset", options: { offset: [10, -2] } }] },
              }}
            >
              {item?.creator
                ? item?.creator?.name.length > 17
                  ? item?.creator?.name?.slice(0, 11) + "..."
                  : item?.creator?.name
                : "Self"}
            </Tooltip>
            <Box component={"div"} sx={{ display: "none !important" }} className="pdf-show">
              {item?.creator ? item?.creator?.name : "Self"}
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.name ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: { modifiers: [{ name: "offset", options: { offset: [10, -2] } }] },
              }}
            >
              {item?.name ?? "-"}
            </Tooltip>
            <Box component={"div"} sx={{ display: "none !important" }} className="pdf-show">
              {item?.name ?? "-"}
            </Box>
          </Box>
        );
      case 3:
        return item?.id ?? "-";
      case 4:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.email ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: { modifiers: [{ name: "offset", options: { offset: [10, -2] } }] },
              }}
            >
              {item?.email
                ? item?.email.length > 20
                  ? item?.email?.slice(0, 20) + "..."
                  : item?.email
                : "-"}
            </Tooltip>
            <Box component={"div"} sx={{ display: "none !important" }} className="pdf-show">
              {item?.email ?? "-"}
            </Box>
          </Box>
        );
      case 5:
        return item?.customerProfile?.nationality?.name ?? "-";
      case 6:
        return item?.customerProfile?.is_trusted ? "Trusted" : "Regular";
      case 7:
        return item?.customerProfile?.import_export ? "Both" : item?.business_region ?? "-";
      case 8:
        return item?.branch ? item?.branch?.name : "-";
      case 9:
        return (
          <DropdownMenu
            item={item}
            isOpen={activeMenu === item.id}
            onToggle={(isOpen) => handleMenuToggle(item.id, isOpen)}
            id={item?.id}
            navigate={navigate}
            onClickAssignPermission={() => openPermissionDialogFromRow(item)}
          />
        );
      default:
        return "-";
    }
  };

  useEffect(() => {
    getcustomerList();
    getBranches();
    getUsers();
  }, []);

  return (
    <Box sx={{ m: 4 }}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item xs={12} sm="auto">
          <Typography
            variant="h5"
            sx={{ color: Colors.charcoalGrey, fontFamily: FontFamily.NunitoRegular, mb: { xs: 1.5, sm: 0 }, mt: 2 }}
          >
            Mobile Permission
          </Typography>
        </Grid>

        <Grid item xs={12} sm="auto">
          <Box textAlign={{ xs: "left", sm: "right" }}>
            <PrimaryButton
              title="Assign Permission"
              type="button"
              style={{ minWidth: 180 }}
              onClick={() => {
                if (!selectedVehicles?.length) return ErrorToaster("Select a customer first.");
                openPermissionDialogBulk();
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <InputField
            size="small"
            label="Search"
            placeholder="Search"
            register={register2("search", {
              onChange: (e) => handleFilter({ search: e.target.value }),
            })}
          />
        </Grid>

        {customerList && (
          <Grid item xs={12} sm="auto" sx={{ ml: { sm: "auto" }, mt: { xs: 1.5, sm: 0 } }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Columns</InputLabel>
                <Select
                  multiple
                  value={visibleColumns}
                  label="Columns"
                  onChange={handleColumnChange}
                  renderValue={() => "Show/Hide"}
                >
                  {tableHead.map((column, index) => {
                    if (column !== "Select" && column !== "Status") {
                      return (
                        <MenuItem key={index} value={index}>
                          <Checkbox checked={visibleColumns.includes(index)} />
                          <ListItemText primary={column} />
                        </MenuItem>
                      );
                    }
                    return null;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid item md={11}>
        {customerList && (
          <Box>
            <Fragment>
              <Box className="pdf-show" sx={{ display: "none" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="h5"
                    sx={{ color: Colors.charcoalGrey, fontFamily: FontFamily.NunitoRegular, mb: 2 }}
                  >
                    Galaxy Customers
                  </Typography>
                  <Box sx={{ fontWeight: 400, fontSize: "12px", mt: 1.5, color: Colors.charcoalGrey }}>
                    <span>Date: &nbsp;&nbsp;</span>
                    {moment().format("MM-DD-YYYY")}
                  </Box>
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                  borderRadius: 2,
                  maxHeight: "calc(100vh - 330px)",
                }}
                className="table-box"
              >
                <Table stickyHeader sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow className="pdf-table">
                      {visibleColumns.map((index) => (
                        <Cell className="pdf-table" key={index}>
                          {tableHead[index] === "Select" ? (
                            <Checkbox
                              className="pdf-hide"
                              sx={{ color: "white !important" }}
                              checked={selectAll}
                              onChange={() => handleAllCheck(!selectAll)}
                            />
                          ) : (
                            tableHead[index]
                          )}
                        </Cell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {!loader ? (
                      customerList?.length ? (
                        customerList?.map((item, rowIndex) => (
                          <Row key={rowIndex} sx={{ bgcolor: rowIndex % 2 !== 0 && "#EEFBEE" }}>
                            {visibleColumns.map((colIndex) => (
                              <Cell className="pdf-table" key={colIndex}>
                                {renderCellContent(colIndex, item)}
                              </Cell>
                            ))}
                          </Row>
                        ))
                      ) : (
                        <Row>
                          <Cell colSpan={tableHead.length + 1} align="center" sx={{ fontWeight: 600 }}>
                            No Data Found
                          </Cell>
                        </Row>
                      )
                    ) : (
                      <TableRow>
                        <TableCell colSpan={visibleColumns?.length + 2} align="center" sx={{ fontWeight: 600 }}>
                          <Box className={classes.loaderWrap}>
                            <CircularProgress />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Pagination
                currentPage={currentPage}
                pageSize={pageLimit}
                onPageSizeChange={(size) => getcustomerList(1, size.target.value)}
                tableCount={customerList?.length || 0}
                totalCount={totalCount}
                onPageChange={(page) => getcustomerList(page, "")}
              />
            </Fragment>

            {loader && <CircleLoading />}
          </Box>
        )}
      </Grid>

      <Dialog
        open={permDialogOpen}
        onClose={closePermissionDialog}
        PaperProps={{ sx: { borderRadius: 3, p: 2, width: 420, maxWidth: "90vw" } }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={1} pt={1} mb={1}>
          <Typography variant="h6" sx={{ fontFamily: FontFamily.NunitoRegular, fontWeight: 700 }}>
            Assign & UnAssign Permissions
          </Typography>
          <IconButton size="small" onClick={closePermissionDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        {showAssigneeLine && (
          <Box px={1} pb={2}>
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5, fontFamily: FontFamily.NunitoRegular }}>
              You’re assigning permissions to{" "}
              <Typography component="span" sx={{ fontWeight: 700, color: Colors.charcoalGrey }}>
                {permUser?.name || "-"}
              </Typography>
            </Typography>
          </Box>
        )}

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", borderColor: "#E5E7EB", mx: 1 }}>
          {permLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 1.5, fontFamily: FontFamily.NunitoRegular }}>
                Loading permissions…
              </Typography>
            </Box>
          ) : (
            [
              { key: "dashboard_module", label: "Dashboard" },
              { key: "vehicle_module", label: "Vehicles" },
              { key: "vcc_module", label: "VCC" },
              { key: "payment_module", label: "Payments" },
              { key: "gate_pass_module", label: "Gatepass" },
              { key: "statement_module", label: "Statements" },
              { key: "sub_customer_module", label: "Sub Customers" },
            ].map((row, idx, arr) => (
              <Box key={row.key}>
                <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
                  <Typography sx={{ fontFamily: FontFamily.NunitoRegular }}>{row.label}</Typography>
                  <Switch
                    edge="end"
                    checked={!!(perm)[row.key]}
                    onChange={(e) => setPerm((p) => ({ ...p, [row.key]: e.target.checked }))}
                  />
                </Box>
                {idx !== arr.length - 1 && <Divider sx={{ borderColor: "#E5E7EB" }} />}
              </Box>
            ))
          )}
        </Paper>

        <Box px={1} py={2} display="flex" justifyContent="flex-end">
          <PrimaryButton title="Assign" type="button" style={{ minWidth: 120 }} onClick={handleAssignPermissions} />
        </Box>
      </Dialog>
    </Box>
  );
}

export default MobilePermission;
